import { message } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { GET_TAX_CATEGORY_TREE } from '../../api/MaterialApi'
import { isResponseCodeSuccess } from '../../tools/util'
import {
  collectParentKeys,
  TaxCategoryNode,
  TaxCategoryRow,
  toTableRows,
} from './tableRows'

/**
 * 全量树的会话内缓存。
 *
 * MPM 本地税编库约 4700 条，同一次会话里反复打开弹窗不重复请求；页面刷新即失效。
 * 筛选结果不入缓存，因为它只是全量树的子集。
 */
let fullTreeCache: TaxCategoryNode[] | null = null

export type LoadStatus = 'loading' | 'ok' | 'empty' | 'error'

export type FilterField = 'code' | 'shortName' | 'name'

export const FILTER_FIELDS: { label: string; field: FilterField }[] = [
  { label: '税收分类编码', field: 'code' },
  { label: '简称', field: 'shortName' },
  { label: '税收分类名称', field: 'name' },
]

export interface TaxCategoryFilters {
  code?: string
  shortName?: string
  name?: string
}

/** 选中并确定后回传给调用方的数据，字段取自 MPM 主数据，不做二次推导 */
export interface TaxCategoryPicked {
  code: string
  name: string
  shortName: string
  rate: number | null
  rateRaw: string
}

export interface UseHookParams {
  visible?: boolean
  onConfirm: (picked: TaxCategoryPicked) => void
  onCancel?: () => void
}

const useHook = ({ visible, onConfirm, onCancel }: UseHookParams) => {
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [rows, setRows] = useState<TaxCategoryRow[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [selectedCode, setSelectedCode] = useState('')
  const [filters, setFilters] = useState<TaxCategoryFilters>({})
  const [filtered, setFiltered] = useState(false)
  const flatRef = useRef<Record<string, TaxCategoryNode>>({})

  const applyNodes = (nodes: TaxCategoryNode[], byFilter: boolean) => {
    const flat: Record<string, TaxCategoryNode> = {}
    const data = toTableRows(nodes, flat)
    flatRef.current = flat
    setRows(data)
    setFiltered(byFilter)
    if (byFilter) {
      // 筛选结果只含命中行与其祖先，全部展开才能看到命中行
      setExpandedRowKeys(collectParentKeys(data))
    } else {
      setExpandedRowKeys(data.map((row) => row.key))
    }
    setStatus(data.length ? 'ok' : 'empty')
  }

  const load = async (params?: TaxCategoryFilters) => {
    const byFilter = Boolean(
      params && (params.code || params.name || params.shortName),
    )
    if (!byFilter && fullTreeCache) {
      applyNodes(fullTreeCache, false)
      return
    }
    setStatus('loading')
    const res = await GET_TAX_CATEGORY_TREE(params)
    // 调用失败由请求层统一提示，这里只切失败态提供重试；MPM 侧调用失败不会返回空数组，
    // 所以「空」只可能是筛选无命中，不会把失败误当成没有数据
    if (!isResponseCodeSuccess(res.code)) {
      setStatus('error')
      return
    }
    const nodes: TaxCategoryNode[] = res.data || []
    if (!byFilter) {
      fullTreeCache = nodes
    }
    applyNodes(nodes, byFilter)
  }

  const buildParams = (): TaxCategoryFilters | undefined => {
    const code = (filters.code || '').trim()
    const shortName = (filters.shortName || '').trim()
    const name = (filters.name || '').trim()
    if (!code && !shortName && !name) {
      return undefined
    }
    const params: TaxCategoryFilters = {}
    if (code) {
      params.code = code
    }
    if (shortName) {
      params.shortName = shortName
    }
    if (name) {
      params.name = name
    }
    return params
  }

  // 每次打开都回到全量树、清掉上次的筛选条件与选中项
  useEffect(() => {
    if (!visible) {
      return
    }
    setSelectedCode('')
    setFilters({})
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const setFilter = (field: FilterField, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleSearch = () => {
    setSelectedCode('')
    load(buildParams())
  }

  const handleReset = () => {
    setFilters({})
    setSelectedCode('')
    load()
  }

  const handleRetry = () => load(buildParams())

  /** 只有可选行能被选中，不可选行整行点击无效 */
  const handleSelectRow = (row: TaxCategoryRow) => {
    if (!row.selectable) {
      return
    }
    setSelectedCode(row.code)
  }

  /**
   * 整行点击：有下级的行交给表格自己展开收起（expandRowByClick），这里只处理末级行的选中。
   *
   * 不在有下级的行上顺带选中：一次点击既展开又选中会让人不知道自己选了什么；
   * 这类行仍可点行首单选按钮选中。
   */
  const handleRowClick = (row: TaxCategoryRow) => {
    if (row.children && row.children.length) {
      return
    }
    handleSelectRow(row)
  }

  const handleConfirm = () => {
    const node = flatRef.current[selectedCode]
    if (!node) {
      message.warning('请选择税收分类编码')
      return
    }
    if (!node.selectable) {
      message.warning('该编码没有匹配到对应的物料税率对应值，请另选编码')
      return
    }
    onConfirm({
      code: node.code,
      name: node.name,
      shortName: node.shortName || '',
      rate: node.rate,
      rateRaw: node.rateRaw || '',
    })
    onCancel?.()
  }

  return {
    status,
    rows,
    expandedRowKeys,
    selectedCode,
    filters,
    filtered,
    setExpandedRowKeys,
    setFilter,
    handleSearch,
    handleReset,
    handleRetry,
    handleSelectRow,
    handleRowClick,
    handleConfirm,
  }
}

export default useHook
import { message } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { GET_TAX_CATEGORY_TREE } from '../../api/MaterialApi'
import { isResponseCodeSuccess } from '../../tools/util'
import {
  collectParentKeys,
  TaxCategoryNode,
  TaxCategoryRow,
  toTableRows,
} from './tableRows'

/**
 * 全量树的会话内缓存。
 *
 * MPM 本地税编库约 4700 条，同一次会话里反复打开弹窗不重复请求；页面刷新即失效。
 * 筛选结果不入缓存，因为它只是全量树的子集。
 */
let fullTreeCache: TaxCategoryNode[] | null = null

export type LoadStatus = 'loading' | 'ok' | 'empty' | 'error'

export type FilterField = 'code' | 'shortName' | 'name'

export const FILTER_FIELDS: { label: string; field: FilterField }[] = [
  { label: '税收分类编码', field: 'code' },
  { label: '简称', field: 'shortName' },
  { label: '税收分类名称', field: 'name' },
]

export interface TaxCategoryFilters {
  code?: string
  shortName?: string
  name?: string
}

/** 选中并确定后回传给调用方的数据，字段取自 MPM 主数据，不做二次推导 */
export interface TaxCategoryPicked {
  code: string
  name: string
  shortName: string
  rate: number | null
  rateRaw: string
}

export interface UseHookParams {
  visible?: boolean
  onConfirm: (picked: TaxCategoryPicked) => void
  onCancel?: () => void
}

const useHook = ({ visible, onConfirm, onCancel }: UseHookParams) => {
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [rows, setRows] = useState<TaxCategoryRow[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [selectedCode, setSelectedCode] = useState('')
  const [filters, setFilters] = useState<TaxCategoryFilters>({})
  const [filtered, setFiltered] = useState(false)
  const flatRef = useRef<Record<string, TaxCategoryNode>>({})

  const applyNodes = (nodes: TaxCategoryNode[], byFilter: boolean) => {
    const flat: Record<string, TaxCategoryNode> = {}
    const data = toTableRows(nodes, flat)
    flatRef.current = flat
    setRows(data)
    setFiltered(byFilter)
    if (byFilter) {
      // 筛选结果只含命中行与其祖先，全部展开才能看到命中行
      setExpandedRowKeys(collectParentKeys(data))
    } else {
      setExpandedRowKeys(data.map((row) => row.key))
    }
    setStatus(data.length ? 'ok' : 'empty')
  }

  const load = async (params?: TaxCategoryFilters) => {
    const byFilter = Boolean(
      params && (params.code || params.name || params.shortName),
    )
    if (!byFilter && fullTreeCache) {
      applyNodes(fullTreeCache, false)
      return
    }
    setStatus('loading')
    const res = await GET_TAX_CATEGORY_TREE(params)
    // 调用失败由请求层统一提示，这里只切失败态提供重试；MPM 侧调用失败不会返回空数组，
    // 所以「空」只可能是筛选无命中，不会把失败误当成没有数据
    if (!isResponseCodeSuccess(res.code)) {
      setStatus('error')
      return
    }
    const nodes: TaxCategoryNode[] = res.data || []
    if (!byFilter) {
      fullTreeCache = nodes
    }
    applyNodes(nodes, byFilter)
  }

  const buildParams = (): TaxCategoryFilters | undefined => {
    const code = (filters.code || '').trim()
    const shortName = (filters.shortName || '').trim()
    const name = (filters.name || '').trim()
    if (!code && !shortName && !name) {
      return undefined
    }
    const params: TaxCategoryFilters = {}
    if (code) {
      params.code = code
    }
    if (shortName) {
      params.shortName = shortName
    }
    if (name) {
      params.name = name
    }
    return params
  }

  // 每次打开都回到全量树、清掉上次的筛选条件与选中项
  useEffect(() => {
    if (!visible) {
      return
    }
    setSelectedCode('')
    setFilters({})
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const setFilter = (field: FilterField, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleSearch = () => {
    setSelectedCode('')
    load(buildParams())
  }

  const handleReset = () => {
    setFilters({})
    setSelectedCode('')
    load()
  }

  const handleRetry = () => load(buildParams())

  /** 只有可选行能被选中，不可选行整行点击无效 */
  const handleSelectRow = (row: TaxCategoryRow) => {
    if (!row.selectable) {
      return
    }
    setSelectedCode(row.code)
  }

  /**
   * 整行点击：有下级的行交给表格自己展开收起（expandRowByClick），这里只处理末级行的选中。
   *
   * 不在有下级的行上顺带选中：一次点击既展开又选中会让人不知道自己选了什么；
   * 这类行仍可点行首单选按钮选中。
   */
  const handleRowClick = (row: TaxCategoryRow) => {
    if (row.children && row.children.length) {
      return
    }
    handleSelectRow(row)
  }

  const handleConfirm = () => {
    const node = flatRef.current[selectedCode]
    if (!node) {
      message.warning('请选择税收分类编码')
      return
    }
    if (!node.selectable) {
      message.warning('该编码没有匹配到对应的物料税率对应值，请另选编码')
      return
    }
    onConfirm({
      code: node.code,
      name: node.name,
      shortName: node.shortName || '',
      rate: node.rate,
      rateRaw: node.rateRaw || '',
    })
    onCancel?.()
  }

  return {
    status,
    rows,
    expandedRowKeys,
    selectedCode,
    filters,
    filtered,
    setExpandedRowKeys,
    setFilter,
    handleSearch,
    handleReset,
    handleRetry,
    handleSelectRow,
    handleRowClick,
    handleConfirm,
  }
}

export default useHook
